import { Link } from "react-router-dom";
import FlightDealHighlights from "@/components/landing/FlightDealHighlights";
import CheapDealShelf from "@/components/landing/CheapDealShelf";
import FlightSearchDifference from "@/components/landing/FlightSearchDifference";
import SmartFareDemo from "@/components/landing/SmartFareDemo";
import { ArrowRight, ArrowLeftRight, Bell, CircleCheck, Briefcase, Luggage, Mountain, Plane, Route, UserRound } from "lucide-react";
import "@/styles/fareInformation.css";
import "@/styles/landingExplore.css";

const searchLink = "/flight-deals";
const fareInformation = [
  { icon: ArrowLeftRight, title: "Round-trip prices", copy: "Departure and return, together." },
  { icon: UserRound, title: "One adult · Economy", copy: "Prices shown per person." },
  { icon: Briefcase, title: "Checked baggage", copy: "Check checked-bag allowance and fees with the airline." },
  { icon: Route, title: "At most one stop", copy: "Nonstop and one-stop flights searched." },
  { icon: Luggage, title: "One cabin bag", copy: "Included in our search preferences." },
  { icon: CircleCheck, title: "Before you book", copy: "Check flight times, baggage and the final total." },
];

export default function FlightDealsLanding() {
  return (
    <div className="min-h-screen bg-[#EEECE4] text-[#233E34]" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-white focus:p-4 focus:text-black">Skip to content</a>
      <header className="fare-demo-header border-b border-[#294F40]/15">
        <nav aria-label="Main navigation" className="ticket-landing-nav">
          <Link to="/" aria-label="TrailPulse home" className="flex items-center gap-2 text-lg font-extrabold"><svg className="fare-demo-brand" viewBox="0 0 64 42" fill="none" aria-hidden="true"><path d="M3 36 24 11 32 20 40 3 61 35 3 36Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="m8 33 16-22-4 16 12-7 8-17-4 19 8-8 12 20M13 34l11-7-3 8m9 0 10-13 7 13" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>TrailPulse</Link>
          <a href="#fare-highlights" className="ticket-nav-active">Flight deals</a>
        </nav>
      </header>

      <main id="main" className="flight-landing-main">
        <section className="fare-demo-hero mb-4 lg:mb-10">
        <div className="fare-demo-hero-inner">
          <div className="fare-demo-copy">
            <h1>Best Deal Chaser<br /></h1><h1><span>Find the fare that fits.</span><br /></h1><h3><span>Go wherever you want to go.</span></h3>
            <Link to={searchLink} className="fare-demo-cta">Sign in <ArrowRight className="h-5 w-5" aria-hidden="true" /></Link>
            <div className="fare-demo-route" aria-hidden="true">
              <svg viewBox="0 0 570 64" fill="none"><path d="M15 38C100 -12 152 8 245 38S420 3 550 38" stroke="currentColor" strokeDasharray="5 7" /><circle cx="15" cy="38" r="5" fill="currentColor" /><circle cx="245" cy="38" r="6" fill="#C69D35" /><circle cx="550" cy="38" r="5" fill="currentColor" /></svg>
              <Plane className="fare-demo-route-plane" size={21} />
              <div><span>Delhi</span><span>Somewhere<br />new</span><span>Singapore</span></div>
            </div>
          </div>

          <SmartFareDemo />
        </div>
        <div className="fare-demo-steps">
          <div><b>1</b><div><strong>Choose your departure</strong><p>Start from your airport.</p></div></div>
          <div><b>2</b><div><strong>Set your flexibility</strong><p>A month, exact dates, or Anywhere.</p></div></div>
          <div><b>3</b><div><strong>Find the fare that fits</strong><p>Explore your options, then choose your trip.</p></div></div>
        </div>
        </section>

        <FlightDealHighlights />
        <section className="fare-information" aria-labelledby="fare-information-title">
          <div className="fare-information-heading">
            <h2 id="fare-information-title">What goes into these fares?</h2>
            <p>A quick look at how we search.</p>
          </div>
          <div className="fare-information-grid">
            {fareInformation.map(({ icon: Icon, title, copy }) => (
              <div className="fare-information-item" key={title}>
                <Icon size={21} aria-hidden="true" />
                <div><h3>{title}</h3><p>{copy}</p></div>
              </div>
            ))}
          </div>
          <p className="fare-information-note">Cabin bag size and weight depend on the airline and fare. Confirm baggage allowance and final price when booking.</p>
        </section>
        <CheapDealShelf />
        <section aria-labelledby="why-flights" className="landing-explore">
          <div className="landing-explore-heading">
            <p className="landing-explore-eyebrow">The TrailPulse difference</p>
            <h2 id="why-flights">What makes TrailPulse Better?</h2>
            <p>See what happens when the search starts with your plans.</p>
          </div>
          <FlightSearchDifference />
          <div className="landing-alert-upcoming"><Bell size={21} aria-hidden="true" /><div><span>Coming soon</span><h3>Price alerts, alongside your search.</h3><p>We plan to add fare notifications so you can discover deals and search your own plans in one place. Alerts are not available yet.</p></div></div>
          <div className="landing-comparison-action"><p>Discover a deal. Or find an option around your plans.</p><Link to={searchLink}>Search your trip <ArrowRight size={17} aria-hidden="true" /></Link></div>
        </section>

        <section className="landing-closing" aria-labelledby="landing-closing-title">
          <div className="landing-closing-copy">
            <p className="landing-explore-eyebrow">A little curiosity. A new destination.</p>
            <h2 id="landing-closing-title">Give your next trip a starting point.</h2>
            <p>Explore flight options for the places you love and the ones you haven?t discovered yet.</p>
          </div>
          <div className="landing-closing-action">
            <Link to={searchLink}>Find flight deals <ArrowRight size={19} aria-hidden="true" /></Link>
            <span>Search a route. Discover somewhere new.</span>
          </div>
          <p className="landing-closing-note">Fares and availability can change. Confirm the final price with the booking provider.</p>
        </section>
      </main>
      <footer className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-8 text-sm text-[#596B60] sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <span className="flex items-center gap-2 font-semibold text-[#294F40]"><Mountain className="h-4 w-4 text-[#28634D]" aria-hidden="true" />TrailPulse</span>
        <p>Support: <a href="mailto:hello@trailpulse.in" className="font-semibold text-[#28634D] underline underline-offset-4">hello@trailpulse.in</a></p>
        <p>&copy; {new Date().getFullYear()} TrailPulse. Your next journey starts here.</p>
      </footer>
    </div>
  );
}
